import React, { useState, useEffect, createContext, useContext, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import IssueReporter from "@/components/IssueReporter";
import NotificationBell from "@/components/NotificationBell";
import IntegrationSetupNudge from "@/components/IntegrationSetupNudge";
import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import AppVersionWatcher from "@/components/AppVersionWatcher";

// Remove platform badge
if (typeof document !== 'undefined') {
  const badge = document.getElementById('emergent-badge');
  if (badge) badge.remove();
}

// CRM Pages
import Login from "@/pages/Login";
import AcceptInvite from "@/pages/AcceptInvite";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
// PD-12 (Feb 12, 2026 PM-3) — lazy-load every route that pulls in
// `@/data/leadFocusDemoData.js` (43 KB source · ~280 KB after deps).
// Webpack splits each into its own async chunk so the heavy demo
// dataset is no longer in the main bundle — non-demo + non-CRM users
// (login, accept-invite, book-demo) never download it.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Leads = lazy(() => import("@/pages/Leads"));
// Iter 188.12 (Feb 2026) — EHI Clients browse (ehiclient_* collections).
const EHIClients = lazy(() => import("@/pages/EHIClients"));
const EHIClientFocus = lazy(() => import("@/pages/EHIClientFocus"));
const LeadFocus = lazy(() => import("@/pages/LeadFocus"));
const LuminaSearch = lazy(() => import("@/pages/LuminaSearch"));
const IssueReportsAnalyzer = lazy(() => import("@/pages/IssueReportsAnalyzer"));
const SalesDialerLive = lazy(() => import("@/pages/SalesDialerLive"));
const CCMDialerSandbox = lazy(() => import("@/pages/CCMDialerSandbox"));
import Contacts from "@/pages/Contacts";
import ContactFocusView from "@/pages/ContactFocusView";
import AddLead from "@/pages/AddLead";
import Quotes from "@/pages/Quotes";
import QuoteBuilder from "@/pages/QuoteBuilder";
import Documents from "@/pages/Documents";
import Templates from "@/pages/Templates";
import QuoteDetail from "@/pages/QuoteDetail";
import Communications from "@/pages/Communications";
import CalendarPage from "@/pages/CalendarPage";
import BulkCoaching from "@/pages/BulkCoaching";
import DigitalAssets from "@/pages/DigitalAssets";
import AIAgents from "@/pages/AIAgents";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Audiences from "@/pages/Audiences";
import { RuntimeConfigProvider, useRuntimeConfig } from "@/lib/runtimeConfig";
import ContractSign from "@/pages/ContractSign";
import BookDemo from "@/pages/BookDemo";
import ContractBuilder from "@/pages/ContractBuilder";
import DemoBookings from "@/pages/DemoBookings";
import ClientsDialer from "@/pages/ClientsDialer";
import ClientLiveDialer from "@/pages/ClientLiveDialer";
import Meetings from "@/pages/Meetings";
import SalesDialer from "@/pages/SalesDialer";
import AdminRepActivity from "@/pages/AdminRepActivity";
import UploadPortal from "@/pages/UploadPortal";

// Marketing Hub — drop-in routes module (16 pages × 5 modes)
import { MarketingRoutes } from "@/marketing-hub.routes";

// Components
import Sidebar from "@/components/Sidebar";
import { IncomingCallOverlayProvider } from "@/components/rc/IncomingCallOverlay";
import GlobalWebPhoneDock from "@/components/rc/GlobalWebPhoneDock";
import IncompleteDispositionsNotifier from "@/components/IncompleteDispositionsNotifier";
import FloatingLiveCallCoach from "@/components/dialer/FloatingLiveCallCoach";
import AIChatFab from "@/components/AIChatFab";
import CmdKPalette from "@/components/CmdKPalette";
import RequireCapability from "@/components/RequireCapability";
import RequireSuperAdmin from "@/components/RequireSuperAdmin";
import { TrainingProvider } from "@/lib/trainingContext";
import { ConfirmProvider } from "@/hooks/useConfirm";
import { PromptProvider } from "@/hooks/usePrompt";
import TrainingRoot from "@/components/training/TrainingRoot";
import { loadFieldOptions } from "@/lib/leadFieldOptions";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Feature flag — when false, /marketing/* routes are redirected home and
// sidebar links are hidden. Default OFF so production builds don't ship
// the half-built Marketing module unless ops flips it on explicitly.
// Override by setting REACT_APP_ENABLE_MARKETING=true in the build env.
export const MARKETING_ENABLED = String(process.env.REACT_APP_ENABLE_MARKETING || "false").toLowerCase() === "true";
// Phase-1 hardening (Apr 2026): dynamic-fetched feature flag.
// Backend `/api/config` is the source of truth; this default keeps the
// page navigable in dev where the env flag is on by default.
export const AUDIENCE_BUILDER_ENABLED_DEFAULT = String(process.env.REACT_APP_AUDIENCE_BUILDER_ENABLED || "true").toLowerCase() === "true";

// Auth Context
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);
  // Backend capability map — fetched from /api/auth/permissions after auth
  // bootstrap. `null` while loading; `permsError` true when the fetch fails
  // (consumers must treat that as a hard-restrictive state per the defense-
  // in-depth contract). See `lib/capabilities.js` for the safe fallback.
  const [permissions, setPermissions] = useState(null);
  const [permsError, setPermsError] = useState(false);

  const fetchPermissions = async (t) => {
    try {
      const r = await axios.get(`${API}/auth/permissions`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setPermissions(r.data?.permissions || null);
      setPermsError(!r.data?.permissions);
    } catch {
      setPermissions(null);
      setPermsError(true);
    }
  };

  useEffect(() => {
    const validateToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
          loadFieldOptions({ force: true });
          await fetchPermissions(token);
        } catch {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
          setPermissions(null);
          setPermsError(false);
        }
      } else {
        setPermissions(null);
        setPermsError(false);
      }
      setLoading(false);
    };
    validateToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    // Server may return either: 
    //   (a) {access_token, user} — immediate login
    //   (b) {mfa_required: true, challenge_token, mfa_enrolled} — require 2FA
    if (response.data?.mfa_required) {
      // Bubble up; the Login page handles the MFA challenge UI.
      return { mfa_required: true, challenge_token: response.data.challenge_token, mfa_enrolled: response.data.mfa_enrolled };
    }
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    // Iter 237 — clear per-session integration nudge dismissal so the
    // post-login modal re-appears for reps who still haven't connected.
    try { sessionStorage.removeItem("integration_nudge_dismissed_v1"); } catch (_) {}
    await fetchPermissions(response.data.access_token);
    return response.data;
  };

  const verifyMfa = async ({ challenge_token, code }) => {
    const response = await axios.post(`${API}/auth/mfa/verify`, { challenge_token, code });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    try { sessionStorage.removeItem("integration_nudge_dismissed_v1"); } catch (_) {}
    await fetchPermissions(response.data.access_token);
    return response.data;
  };

  const register = async (email, password, full_name, role = "sales") => {
    const response = await axios.post(`${API}/auth/register`, { email, password, full_name, role });
    localStorage.setItem("token", response.data.access_token);
    setToken(response.data.access_token);
    setUser(response.data.user);
    await fetchPermissions(response.data.access_token);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
    setPermissions(null);
    setPermsError(false);
  };

  /**
   * Refresh the cached `user` object from /auth/me. Call after a profile
   * save (Settings → Account) so dependent surfaces (signature preview,
   * top-bar avatar, etc.) see the new title/phone without a logout.
   */
  const refreshUser = async () => {
    if (!token) return null;
    try {
      const r = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
      setUser(r.data);
      return r.data;
    } catch {
      return null;
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, verifyMfa, register, logout, refreshUser, permissions, permsError }}>
      {children}
    </AuthContext.Provider>
  );
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--page-bg)" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 28, height: 28, border: "2.5px solid var(--blue)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 10px" }} />
          <p style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
};

// Gate the Audiences page behind the runtime feature flag — when off,
// redirect to /leads (where the audience deep-link banner still works
// for any saved bookmarks). Phase-1 hardening (Apr 2026).
const AudiencesGate = () => {
  const { audienceBuilderEnabled, loaded } = useRuntimeConfig();
  if (!loaded) return null;  // wait for /api/config; avoids flicker
  if (!audienceBuilderEnabled) return <Navigate to="/leads" replace />;
  return <Audiences />;
};

const MainLayout = ({ children }) => {
  // Iter 170.2 (Josh) — right-edge slide-out panels (e.g. Performance
  // Snapshot) overlap the bottom dock in the bottom-right corner. Listen
  // for rightpanel:open / rightpanel:close events so the dock slides
  // left out of the way while the panel is open.
  // Bottom-dock collapse/expand (Iter · Jun 2026). Local browser preference;
  // default expanded. An incoming/active call force-expands so the softphone
  // is never hidden; when the call ends we revert to the saved choice.
  const [dockCollapsed, setDockCollapsed] = React.useState(() => {
    try { return localStorage.getItem("bottomDockCollapsed") === "1"; } catch { return false; }
  });
  const [dockCallActive, setDockCallActive] = React.useState(false);
  const [dockUnread, setDockUnread] = React.useState(0);
  const dockItemsVisible = !dockCollapsed || dockCallActive;
  const toggleDock = React.useCallback(() => {
    setDockCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem("bottomDockCollapsed", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  }, []);
  // Track active/incoming calls to force-expand. `webphone:call-started/ended`
  // cover an active (answered) call; the RC iframes also post `rc-call-*-notify`
  // messages we watch so a *ringing* inbound call expands the dock too.
  React.useEffect(() => {
    const onStart = () => setDockCallActive(true);
    const onEnd = () => setDockCallActive(false);
    // Iter 261.1 (bugfix) — an explicit dial (webphone:dial) must ALSO
    // force the bottom-center dock open. Otherwise, when the dock is
    // tucked away, the softphone expands INSIDE the hidden (opacity:0,
    // pointer-events:none) items wrapper — the number dials but the user
    // can't see the ringing/connected state or hang up. Popping the dock
    // open on dial makes the softphone visible + interactive immediately.
    const onDial = () => setDockCallActive(true);
    const onMsg = (ev) => {
      const t = ev?.data?.type;
      if (t === "rc-call-ring-notify" || t === "rc-call-start-notify") setDockCallActive(true);
      else if (t === "rc-call-end-notify") setDockCallActive(false);
    };
    const onUnread = (e) => setDockUnread((e && e.detail && e.detail.unread) || 0);
    window.addEventListener("webphone:call-started", onStart);
    window.addEventListener("webphone:call-ended", onEnd);
    window.addEventListener("webphone:dial", onDial);
    window.addEventListener("message", onMsg);
    window.addEventListener("notifications:unread", onUnread);
    return () => {
      window.removeEventListener("webphone:call-started", onStart);
      window.removeEventListener("webphone:call-ended", onEnd);
      window.removeEventListener("webphone:dial", onDial);
      window.removeEventListener("message", onMsg);
      window.removeEventListener("notifications:unread", onUnread);
    };
  }, []);
  return (
    <TrainingProvider>
      <IncomingCallOverlayProvider>
        <div className="app-container">
          <Sidebar />
          <main className="main-content">
            <TrainingRoot />
            {children}
          </main>
        </div>
        {/* Iter 237 — post-login integration setup nudge. Renders as a
            session-persistent modal for reps who haven't connected M365
            or RC. Dismissible ("Skip for now") but returns on next
            login. Skipped on /settings (redundant there). */}
        <IntegrationSetupNudge />
        {/* Bottom-center peek dock (Iter · Jun 2026). Relocated out of the
            bottom-right corner (which overlapped pagination/action bars) to a
            centered "pull tab": a thin sliver flush to the bottom edge slides a
            horizontal pill toolbar UP on click. Holds AI · Notif · Bug ·
            page-slot · RC softphone. The RC/AI panels still render as their own
            fixed overlays. */}
        <div
          data-testid="bottom-dock"
          style={{
            position: "fixed", left: "50%", bottom: 0, transform: "translateX(-50%)",
            zIndex: 9700,
            display: "flex", flexDirection: "column", alignItems: "center",
            pointerEvents: "none",
            maxWidth: "100vw",
          }}
        >
          <div
            data-testid="bottom-dock-items"
            style={{
              pointerEvents: dockItemsVisible ? "auto" : "none",
              display: "flex", alignItems: "center", gap: 6,
              padding: 6, borderRadius: 999,
              background: "rgba(255,255,255,0.65)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              border: "0.5px solid rgba(0,0,0,0.08)",
              boxShadow: "0 4px 16px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
              marginBottom: 6,
              maxWidth: "calc(100vw - 24px)",
              transform: dockItemsVisible ? "translateY(0)" : "translateY(160%)",
              opacity: dockItemsVisible ? 1 : 0,
              transition: "transform 300ms cubic-bezier(0.22,1,0.36,1), opacity 200ms ease",
            }}
          >
            <AIChatFab />
            <NotificationBell />
            <IssueReporter inline />
            {/* Iter 346 — portal slot for page-scoped dock icons (e.g. the
                Live Notes FAB from SalesDialerLive). Sits between the
                global widgets and the RC softphone pill so the visual
                order is: AI · Notif · Bug · <page-scoped> · RC.
                Empty on pages that don't project anything into it. */}
            <div
              id="bottom-dock-page-slot"
              data-testid="bottom-dock-page-slot"
              style={{ display: "flex", alignItems: "center", gap: 6 }}
            />
            <GlobalWebPhoneDock />
          </div>
          {/* Peek handle — the single control. Flush to the bottom edge,
              horizontally centered, always visible. Chevron points UP when
              tucked (click to reveal) and DOWN when open (click to tuck). */}
          <button
            data-testid="bottom-dock-toggle"
            onClick={toggleDock}
            aria-label={dockItemsVisible ? "Hide quick actions" : "Show quick actions"}
            title={dockItemsVisible ? "Hide quick actions" : "Show quick actions"}
            style={{
              pointerEvents: "auto",
              position: "relative",
              width: 64, height: 20, padding: 0,
              borderTopLeftRadius: 12, borderTopRightRadius: 12,
              borderBottomLeftRadius: 0, borderBottomRightRadius: 0,
              background: "rgba(255,255,255,0.85)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              border: "0.5px solid rgba(0,0,0,0.08)", borderBottom: "none",
              boxShadow: "0 -2px 10px rgba(0,0,0,0.06)",
              cursor: "pointer", color: "#334155",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              {dockItemsVisible ? <polyline points="6 9 12 15 18 9" /> : <polyline points="18 15 12 9 6 15" />}
            </svg>
            {!dockItemsVisible && dockUnread > 0 && (
              <span
                data-testid="bottom-dock-toggle-unread-dot"
                style={{
                  position: "absolute", top: -3, right: 8,
                  width: 8, height: 8, borderRadius: "50%",
                  background: "#ef4444", border: "1.5px solid white",
                }}
              />
            )}
          </button>
        </div>
        <FloatingLiveCallCoach />
        {/* Iter 210.7 · Global "unfinished after-call work" notifier.
            Renders nothing when there are no incomplete dispositions.
            Read-only surfacing — never mutates dispositions itself. */}
        <IncompleteDispositionsNotifier />
        <CmdKPalette />
      </IncomingCallOverlayProvider>
    </TrainingProvider>
  );
};

const P = ({ element }) => {
  // Iter 159 (Josh — Lisa blank /contacts 2026-06-03): every authenticated
  // route now passes through a RouteErrorBoundary so a runtime crash in
  // one page shows an actionable reset card instead of a fully-blank app.
  // Pass `locationKey` so the boundary auto-resets on navigation.
  const location = useLocation();
  return (
    <ProtectedRoute>
      <MainLayout>
        <RouteErrorBoundary locationKey={location.pathname + location.search}>
          {element}
        </RouteErrorBoundary>
      </MainLayout>
    </ProtectedRoute>
  );
};

function App() {
  // PD-12: Suspense fallback for lazy-loaded routes. Reuse the same
  // spinner used by ProtectedRoute's loading state so chunk-loading
  // doesn't flash a different visual.
  const RouteFallback = (
    <div style={{ minHeight: "60vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--page-bg)" }} data-testid="route-suspense-fallback">
      <div style={{ textAlign: "center" }}>
        <div style={{ width: 28, height: 28, border: "2.5px solid var(--blue)", borderTopColor: "transparent", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 10px" }} />
        <p style={{ fontSize: 12, color: "var(--text-tertiary)" }}>Loading…</p>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
  return (
    <AuthProvider>
      <RuntimeConfigProvider>
      <ConfirmProvider>
      <PromptProvider>
      <BrowserRouter>
        <Suspense fallback={RouteFallback}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/accept-invite/:token" element={<AcceptInvite />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:token" element={<ResetPassword />} />
          <Route path="/sign/:id" element={<ContractSign />} />
          <Route path="/book-demo" element={<BookDemo />} />

          {/* CRM Routes */}
          <Route path="/" element={<P element={<Dashboard />} />} />
          <Route path="/contacts" element={<P element={<Contacts />} />} />
          {/* Iter 167.e — /customers is the user-facing route. /contacts is
              kept as a permanent alias so existing bookmarks + saved
              notifications continue to work. */}
          <Route path="/customers" element={<P element={<Contacts />} />} />
          <Route path="/customers/:id" element={<P element={<ContactFocusView />} />} />
          <Route path="/leads" element={<P element={<Leads />} />} />
          {/* Iter 188.12 — EHI Clients (Enable Healthcare's own client roster). */}
          <Route path="/ehi-clients" element={<P element={<EHIClients />} />} />
          <Route path="/ehi-clients/:client_id" element={<P element={<EHIClientFocus />} />} />
          <Route path="/lumina-search" element={<P element={<LuminaSearch />} />} />
          <Route path="/issue-reports-analyzer" element={<P element={<IssueReportsAnalyzer />} />} />
          <Route path="/audiences" element={<Navigate to="/leads?view=segments" replace />} />
          <Route path="/segments" element={<Navigate to="/leads?view=segments" replace />} />
          <Route path="/lead-focus/:id" element={<P element={<LeadFocus />} />} />
          <Route path="/contact/:id" element={<P element={<ContactFocusView />} />} />
          <Route path="/add-lead" element={<P element={<AddLead />} />} />
          {/* Iter 334 — Restored per user request: Sidebar's "Quotes &
              Contracts" lands on the LIST view. The builder is reached
              via the "+ New Quote" button on the list, per standard CRM
              nav. Iter 331.1's builder-as-landing has been reverted. */}
          <Route path="/quotes" element={<P element={<Quotes />} />} />
          <Route path="/quotes/list" element={<Navigate to="/quotes" replace />} />
          <Route path="/quotes/new" element={<P element={<QuoteBuilder />} />} />
          <Route path="/contracts/new" element={<P element={<ContractBuilder />} />} />
          <Route path="/tasks" element={<Navigate to="/communications?tab=tasks" replace />} />
          <Route path="/admin/coaching" element={<P element={<BulkCoaching />} />} />
          <Route path="/quotes/:id" element={<P element={<QuoteDetail />} />} />
          <Route path="/quotes/:id/edit" element={<P element={<QuoteBuilder />} />} />
          <Route path="/documents" element={<P element={<Documents />} />} />
          <Route path="/templates" element={<P element={<RequireCapability cap="templates"><Templates /></RequireCapability>} />} />
          <Route path="/templates/settings" element={<P element={<RequireCapability cap="templates"><Templates /></RequireCapability>} />} />
          <Route path="/templates/:templateId" element={<P element={<RequireCapability cap="templates"><Templates /></RequireCapability>} />} />
          <Route path="/communications" element={<P element={<Communications />} />} />
          <Route path="/calendar" element={<P element={<CalendarPage />} />} />
          <Route path="/demo-bookings" element={<P element={<DemoBookings />} />} />
          <Route path="/ehi-demo-bookings" element={<P element={<DemoBookings scope="client" key="client" />} />} />
          <Route path="/meetings" element={<Navigate to="/demo-bookings" replace />} />
          <Route path="/dialer" element={<P element={<RequireCapability cap="dialer"><SalesDialer /></RequireCapability>} />} />
          <Route path="/clients-dialer" element={<P element={<ClientsDialer />} />} />
          <Route path="/clients-dialer/live" element={<P element={<ClientLiveDialer />} />} />
          <Route path="/dialer/live" element={<P element={<RequireCapability cap="dialer"><SalesDialerLive /></RequireCapability>} />} />
          {/* CCM Dialer — Developmental Sandbox (super_admin only). */}
          <Route path="/ccm-dialer" element={<P element={<RequireSuperAdmin><CCMDialerSandbox /></RequireSuperAdmin>} />} />
          <Route path="/dialer-demo" element={<Navigate to="/dialer/live" replace />} />
          <Route path="/dialer-legacy" element={<Navigate to="/dialer" replace />} />
          <Route path="/comms-dashboard" element={<Navigate to="/reports?tab=comms" replace />} />
          <Route path="/upload-portal" element={<P element={<UploadPortal />} />} />
          <Route path="/resources" element={<P element={<DigitalAssets />} />} />
          <Route path="/ai-agents" element={<P element={<RequireCapability cap="ai_agents"><AIAgents /></RequireCapability>} />} />
          <Route path="/reports" element={<P element={<RequireCapability cap="reports"><Reports /></RequireCapability>} />} />
          <Route path="/admin/rep-activity" element={<P element={<RequireCapability cap="reports"><AdminRepActivity /></RequireCapability>} />} />
          <Route path="/settings" element={<P element={<Settings />} />} />
          <Route path="/settings/field-options" element={<Navigate to="/settings?section=field-options" replace />} />

          {/* Marketing Hub — drop-in routes (Plan / Create / Distribute /
              Measure / Agents). Mounted via the shared routes module so
              the IA stays in one place. Gated by BOTH:
              (1) REACT_APP_ENABLE_MARKETING build-flag, AND
              (2) <RequireSuperAdmin> runtime gate passed in via `Gate`. */}
          {MarketingRoutes({
            ProtectedRoute,
            MainLayout,
            Gate: RequireSuperAdmin,
          })}

          {/* Legacy redirects */}
          <Route path="/campaigns" element={<Navigate to={MARKETING_ENABLED ? "/marketing/plan/campaigns" : "/"} replace />} />
          <Route path="/social" element={<Navigate to={MARKETING_ENABLED ? "/marketing/distribute/channels" : "/"} replace />} />
          <Route path="/assets" element={<Navigate to="/resources" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
        <Toaster position="top-right" richColors />
        <AppVersionWatcher />
      </BrowserRouter>
      </PromptProvider>
      </ConfirmProvider>
      </RuntimeConfigProvider>
    </AuthProvider>
  );
}

export default App;
